import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { COUNTRY_CODES, STATUS_OPTIONS, BRAND } from '../mock/mock';
import { ArrowRight, Upload, CheckCircle2, FileText, User, ArrowLeft } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { api } from '../lib/api';

const PortalHeader = () => (
  <header className="border-b border-[#0B5E2A]/10 bg-white/80 backdrop-blur sticky top-0 z-40">
    <div className="max-w-4xl mx-auto container-px h-[64px] flex items-center justify-between">
      <Link to="/" className="flex items-center gap-3">
        <img src={BRAND.logo} alt="HCC" className="h-9 w-9 rounded-full" />
        <div className="leading-tight">
          <div className="font-display text-[15px] font-bold brand-green">{BRAND.name}</div>
          <div className="text-[9px] tracking-[0.22em] uppercase text-brand-gold font-semibold">{BRAND.full}</div>
        </div>
      </Link>
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#4b5e4d] hover:text-[#0B5E2A] transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to portal
      </Link>
    </div>
  </header>
);

const Apply = () => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    name: '', email: '', phone: '', code: '+1', dob: '', nationality: '', status: '',
  });
  const [files, setFiles] = useState([]);
  const [submitted, setSubmitted] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const next = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.dob || !form.nationality || !form.status) {
      toast({ title: 'Missing details', description: 'Please complete all required fields.' });
      return;
    }
    setStep(2);
  };

  const onFiles = (e) => {
    setFiles([...files, ...Array.from(e.target.files || [])]);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const fd = new FormData();
      fd.append('name', form.name);
      fd.append('email', form.email);
      fd.append('phone_code', form.code);
      fd.append('phone', form.phone);
      fd.append('dob', form.dob);
      fd.append('nationality', form.nationality);
      fd.append('status_in_canada', form.status);
      files.forEach((f) => fd.append('documents', f));
      await api.post('/applications', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSubmitted(true);
      toast({ title: 'Application submitted', description: "We'll review it and contact you shortly." });
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Submission failed. Try again.';
      toast({ title: 'Error', description: msg });
    }
  };

  if (submitted) {
    return (
      <main className="min-h-screen bg-[#fdfaf1]">
        <PortalHeader />
        <div className="py-20">
          <div className="max-w-xl mx-auto container-px text-center bg-white border border-[#0B5E2A]/10 rounded-2xl p-10">
            <div className="h-16 w-16 rounded-full bg-brand-green-light mx-auto flex items-center justify-center">
              <CheckCircle2 className="h-9 w-9 brand-green" />
            </div>
            <h2 className="mt-5 font-display text-3xl text-[#0B5E2A]">Application Received</h2>
            <p className="mt-3 text-[#4b5e4d]">Thank you, {form.name.split(' ')[0] || 'applicant'}! Our team will review your application and reach out within 2 business days.</p>
            <Link to="/" className="btn-green inline-flex items-center gap-2 px-6 py-3 rounded-md mt-6">Back to Portal</Link>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#fdfaf1]">
      <PortalHeader />
      <section className="bg-[#f5f9f3] border-b border-[#0B5E2A]/10">
        <div className="max-w-4xl mx-auto container-px py-14">
          <span className="eyebrow">Student Application</span>
          <h1 className="mt-4 font-display text-4xl md:text-5xl text-[#0B5E2A]">Apply Now</h1>
          <p className="mt-3 text-[#4b5e4d]">A short form, then document upload. Takes less than 2 minutes.</p>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-3xl mx-auto container-px">
          <div className="flex items-center gap-3 mb-8">
            <StepPill n={1} active={step >= 1} label="Your Details" icon={User} />
            <div className={`flex-1 h-[2px] ${step > 1 ? 'bg-[#F0B93C]' : 'bg-[#0B5E2A]/15'}`} />
            <StepPill n={2} active={step >= 2} label="Upload Documents" icon={FileText} />
          </div>

          {step === 1 && (
            <form onSubmit={next} className="bg-white border border-[#0B5E2A]/10 rounded-2xl p-7 shadow-sm">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label="Full Name *" name="name" value={form.name} onChange={onChange} full />
                <Field label="Email *" name="email" type="email" value={form.email} onChange={onChange} />
                <div>
                  <label className="block text-sm font-medium text-[#2e4030] mb-1.5">Phone *</label>
                  <div className="flex gap-2">
                    <select name="code" value={form.code} onChange={onChange} className="w-[130px] px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30">
                      {COUNTRY_CODES.map((c) => <option key={c.label} value={c.code}>{c.label}</option>)}
                    </select>
                    <input name="phone" value={form.phone} onChange={onChange} className="flex-1 px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30" />
                  </div>
                </div>
                <Field label="Date of Birth *" name="dob" type="date" value={form.dob} onChange={onChange} />
                <Field label="Nationality *" name="nationality" value={form.nationality} onChange={onChange} />
                <div>
                  <label className="block text-sm font-medium text-[#2e4030] mb-1.5">Status in Canada *</label>
                  <select name="status" value={form.status} onChange={onChange} className="w-full px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30">
                    <option value="">Select status</option>
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <button type="submit" className="btn-green mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-md">Continue to Document Upload <ArrowRight className="h-4 w-4" /></button>
            </form>
          )}

          {step === 2 && (
            <form onSubmit={submit} className="bg-white border border-[#0B5E2A]/10 rounded-2xl p-7 shadow-sm">
              <h3 className="font-display text-xl text-[#0B5E2A] font-bold">Upload Documents</h3>
              <p className="text-sm text-[#4b5e4d] mt-1">Attach your passport, academic transcripts and language test results (optional).</p>

              <label className="mt-5 block border-2 border-dashed border-[#0B5E2A]/25 rounded-xl p-8 text-center cursor-pointer bg-[#f5f9f3] hover:bg-[#edf6ea] transition-colors">
                <Upload className="h-8 w-8 brand-green mx-auto" />
                <div className="mt-2 font-semibold text-[#0B5E2A]">Click to upload or drag files</div>
                <div className="text-xs text-[#6b7e6d]">PDF, JPG, PNG • up to 10MB each</div>
                <input type="file" multiple className="hidden" onChange={onFiles} />
              </label>

              {files.length > 0 && (
                <ul className="mt-4 space-y-2">
                  {files.map((f, i) => (
                    <li key={i} className="flex items-center gap-3 bg-[#f5f9f3] border border-[#0B5E2A]/10 rounded-md px-3 py-2 text-sm">
                      <FileText className="h-4 w-4 brand-green" />
                      <span className="flex-1 truncate">{f.name}</span>
                      <span className="text-xs text-[#6b7e6d]">{Math.ceil(f.size/1024)} KB</span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="mt-6 flex flex-wrap gap-3">
                <button type="button" onClick={() => setStep(1)} className="px-6 py-3 rounded-md border border-[#0B5E2A]/30 text-[#0B5E2A] font-semibold hover:bg-[#f5f9f3] transition-colors">Back</button>
                <button type="submit" className="btn-gold inline-flex items-center gap-2 px-6 py-3 rounded-md">Submit Application <ArrowRight className="h-4 w-4" /></button>
              </div>
            </form>
          )}
        </div>
      </section>
    </main>
  );
};

const StepPill = ({ n, active, label, icon: Icon }) => (
  <div className={`flex items-center gap-2 px-3 py-2 rounded-full border ${active ? 'bg-[#0B5E2A] text-white border-[#0B5E2A]' : 'bg-white text-[#6b7e6d] border-[#0B5E2A]/15'}`}>
    <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${active ? 'bg-[#F0B93C] text-[#06401d]' : 'bg-[#0B5E2A]/10 text-[#6b7e6d]'}`}>{n}</span>
    <span className="text-sm font-semibold">{label}</span>
    <Icon className="h-4 w-4 opacity-70" />
  </div>
);

const Field = ({ label, name, type = 'text', value, onChange, full }) => (
  <div className={full ? 'md:col-span-2' : ''}>
    <label className="block text-sm font-medium text-[#2e4030] mb-1.5">{label}</label>
    <input name={name} type={type} value={value} onChange={onChange} className="w-full px-3 py-2.5 rounded-md border border-[#0B5E2A]/20 focus:outline-none focus:ring-2 focus:ring-[#0B5E2A]/30 text-sm" />
  </div>
);

export default Apply;
